<template>
  <div ref="root" class="TextHider">
    <div v-show="isFullTextVisible && !absoluteBlock" ref="full-text" class="TextHider__full-text">
      <slot></slot>
      <button v-if="isTextOverflowed" class="TextHider__hide-more" @click="hide">
        {{ $t('text_hider.hide_more') }}
      </button>
    </div>

    <div
      v-show="!isFullTextVisible || absoluteBlock"
      :key="hiddenTextUpdaterKey"
      ref="hidden-text"
      class="TextHider__hidden-text"
    >
      <slot></slot>
    </div>

    <div v-show="!isFullTextVisible || absoluteBlock" ref="line-helper" class="TextHider__lines-helper">
      <template v-for="index in lines">
        <br :key="index" />
      </template>
    </div>

    <transition name="full-text">
      <div v-if="isFullTextVisible && absoluteBlock" class="TextHiderAbsoluteBlock">
        <div class="TextHiderAbsoluteBlock__content" @click.stop>
          <slot></slot>
        </div>
        <LLButton class="TextHiderAbsoluteBlock__close-button" round small secondary @click.stop="hide">
          <template #icon-right>
            <CloseRegularIcon class="TextHiderAbsoluteBlock__close-button-icon" />
          </template>
        </LLButton>
      </div>
    </transition>
  </div>
</template>

<script>
import LLButton from '@/components/common/ui-components/LLButton'
import CloseRegularIcon from '@/assets/icons/CloseRegularIcon'
export default {
  name: 'TextHider',
  components: { CloseRegularIcon, LLButton },
  props: {
    lines: { type: Number, default: 10 },
    absoluteBlock: { type: Boolean, default: false }
  },
  data() {
    return {
      isFullTextVisible: false,
      isTextOverflowed: false,
      resizeObserver: null,
      isAbsoluteBlockClosingAnimationRendering: false,
      outsideClickHandler: null,
      hiddenTextUpdaterKey: 0,
      isRenderingNow: false
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.render()
      this.initResizeObserver()
    })
  },
  updated() {
    if (!this.isFullTextVisible) {
      this.render()
    }
  },
  beforeDestroy() {
    this.destroyResizeObserver()
    this.removeOutsideClickListener()
  },
  methods: {
    initResizeObserver() {
      const element = this.$refs.root
      if (!element || typeof ResizeObserver === 'undefined') return

      const resizeObserver = new ResizeObserver(() => {
        if (!this.isFullTextVisible) {
          this.render()
        }
      })

      resizeObserver.observe(element)
      this.resizeObserver = resizeObserver
    },
    destroyResizeObserver() {
      if (this.resizeObserver && this.resizeObserver.disconnect) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
    },
    addOutsideClickListener() {
      if (!this.absoluteBlock || this.outsideClickHandler) return

      const vm = this
      this.outsideClickHandler = function (event) {
        const overlayEl = vm.$el && vm.$el.querySelector('.TextHiderAbsoluteBlock')
        if (!overlayEl) return

        if (!overlayEl.contains(event.target)) {
          vm.hide()
        }
      }

      window.addEventListener('click', this.outsideClickHandler)
    },
    removeOutsideClickListener() {
      if (this.outsideClickHandler) {
        window.removeEventListener('click', this.outsideClickHandler)
        this.outsideClickHandler = null
      }
    },
    showFullText(e) {
      if (e && e.stopPropagation) e.stopPropagation()
      this.isFullTextVisible = true
      if (this.absoluteBlock) {
        this.addOutsideClickListener()
      }
    },
    hide() {
      if (!this.isFullTextVisible) return

      if (this.absoluteBlock) {
        this.isAbsoluteBlockClosingAnimationRendering = true
      }

      this.isFullTextVisible = false
      this.removeOutsideClickListener()

      if (!this.absoluteBlock) {
        this.$nextTick(() => {
          this.render()
        })
      } else {
        setTimeout(() => {
          this.isAbsoluteBlockClosingAnimationRendering = false
          this.render()
        }, 300)
      }
    },
    findPoint(start) {
      const container = this.$refs['hidden-text']
      if (!container) return null

      const tree = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode(node) {
            const parent = node.parentElement
            if (parent && parent.closest && parent.closest('[data-do-not-highlight]')) {
              return NodeFilter.FILTER_REJECT
            }
            return NodeFilter.FILTER_ACCEPT
          }
        },
        false
      )

      let currentNode = tree.nextNode()
      if (!currentNode) return null

      if (start <= 0) {
        return { node: currentNode, offset: 0 }
      }

      let remaining = start
      while (currentNode) {
        const len = (currentNode.nodeValue && currentNode.nodeValue.length) || 0
        if (remaining <= len) {
          return { node: currentNode, offset: remaining }
        }
        remaining -= len
        currentNode = tree.nextNode()
      }

      return null
    },
    refreshHiddenSlot() {
      this.hiddenTextUpdaterKey += 1
    },
    async render() {
      if (this.isAbsoluteBlockClosingAnimationRendering || this.isRenderingNow) return
      this.isRenderingNow = true

      this.refreshHiddenSlot()
      await this.$nextTick()

      const hidden = this.$refs['hidden-text']
      const helper = this.$refs['line-helper']
      if (!hidden || !helper) return

      this.$nextTick(() => {
        const endPoint = this.findPointToCutOverflow()

        if (!endPoint) {
          this.isTextOverflowed = false
          return
        }

        try {
          this.isTextOverflowed = true
          this.cutTextOverflow(endPoint)
          this.placeButton()
        } catch (e) {
          console.error(e)
        }
      })

      this.isRenderingNow = false
    },
    cutTextOverflow(endPoint) {
      const hidden = this.$refs['hidden-text']
      if (!hidden || !endPoint || !endPoint.node) return

      const rangeToCut = document.createRange()
      rangeToCut.setStart(endPoint.node, endPoint.offset)

      const lastChild = hidden.lastChild
      if (lastChild) {
        rangeToCut.setEndAfter(lastChild)
      } else {
        return
      }

      rangeToCut.deleteContents()
    },
    placeButton() {
      const hidden = this.$refs['hidden-text']
      const helper = this.$refs['line-helper']
      if (!hidden || !helper) return

      const textLength = hidden.textContent ? hidden.textContent.length : 0

      const showMoreButton = document.createElement('button')
      showMoreButton.innerHTML = this.$t('text_hider.show_more')
      showMoreButton.classList.add('TextHider__show-more')
      showMoreButton.setAttribute('data-do-not-highlight', '')
      showMoreButton.addEventListener('click', (e) => {
        this.showFullText(e)
      })

      const wrapperSpan = document.createElement('span')
      wrapperSpan.setAttribute('data-do-not-highlight', '')
      wrapperSpan.appendChild(document.createTextNode(''))
      wrapperSpan.appendChild(showMoreButton)

      const walker = document.createTreeWalker(
        hidden,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode(node) {
            return node.nodeValue.trim().length ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP
          }
        },
        false
      )

      let lastTextNode = null
      let current
      while ((current = walker.nextNode())) {
        lastTextNode = current
      }

      if (!lastTextNode) {
        hidden.appendChild(wrapperSpan)
      } else {
        const parent = lastTextNode.parentNode

        if (parent.tagName === 'SPAN' && parent.parentNode) {
          parent.parentNode.insertBefore(wrapperSpan, parent.nextSibling)
        } else {
          parent.insertBefore(wrapperSpan, lastTextNode.nextSibling)
        }
      }

      this.cutTextToFitButton(textLength, wrapperSpan)
    },
    cutTextToFitButton(textLength, span) {
      const helper = this.$refs['line-helper']
      const hidden = this.$refs['hidden-text']
      if (!helper || !hidden) return

      let currentLetter = textLength

      try {
        while (currentLetter > 0) {
          const startPoint = this.findPoint(currentLetter - 1)
          const endPoint = this.findPoint(currentLetter)

          if (!startPoint || !endPoint) break

          const range = document.createRange()
          range.setStart(startPoint.node, startPoint.offset)
          range.setEnd(endPoint.node, endPoint.offset)
          range.deleteContents()

          const referenceRect = helper.getBoundingClientRect()
          const spanRect = span.getBoundingClientRect()

          if (spanRect.bottom <= referenceRect.bottom) {
            return
          }

          currentLetter--
        }
      } catch (e) {
        console.error(e)
      }
    },
    findPointToCutOverflow() {
      const helper = this.$refs['line-helper']
      const hidden = this.$refs['hidden-text']
      if (!helper || hidden == null) return null

      const totalLength = hidden.textContent ? hidden.textContent.length : 0
      if (!totalLength) return null

      const startPoint = this.findPoint(0)
      if (!startPoint) return null

      let endPoint = null

      try {
        for (let i = 0; i < totalLength; i++) {
          endPoint = this.findPoint(i + 1)
          if (!endPoint) break

          const range = document.createRange()
          range.setStart(startPoint.node, startPoint.offset)
          range.setEnd(endPoint.node, endPoint.offset)

          const referenceRect = helper.getBoundingClientRect()
          if (!referenceRect || referenceRect.height === 0) return null

          const rect = range.getBoundingClientRect()
          if (rect.bottom > referenceRect.bottom) {
            return this.findPoint(i)
          }
        }
      } catch (e) {
        console.error(e)
      }

      return null
    }
  }
}
</script>

<style lang="scss" scoped>
.TextHider {
  $root: &;
  @apply relative break-words;
  word-break: break-word;

  &__hidden-text {
    @apply overflow-hidden;
  }

  &__lines-helper {
    @apply absolute top-0 left-0;
  }

  &__hide-more {
    @apply text-accent-01 font-bold;
  }

  &AbsoluteBlock {
    @apply absolute bg-white border-neutral-01-25 shadow-dropdown rounded flex;
    width: calc(100% + 20px);
    max-height: calc(100% + 100px);
    left: -20px;
    top: -20px;
    z-index: 9999;

    &__close-button {
      @apply absolute right-0 top-0 -mr-3 -mt-3 shadow-dropdown;
      width: 24px !important;
      height: 24px !important;

      &-icon {
        @apply w-4 h-4;
      }
    }

    &__content {
      @apply overflow-y-auto py-4 px-8 w-full;
      max-height: 100%;
    }
  }

  .full-text-enter-active,
  .full-text-leave-active {
    transition-duration: 300ms;
    transition-property: opacity, transform;
    transition-timing-function: cubic-bezier(0.98, 0.18, 0, 1);
  }
  .full-text-enter,
  .full-text-leave-to {
    opacity: 0;
    transform: scale(0.95);
  }

  ::v-deep {
    #{$root}__show-more {
      @apply text-accent-01 font-bold;

      &:focus-within {
        @apply outline-none;
      }

      &:before {
        content: '... ';
        @apply text-black;
      }
    }
  }
}
</style>
